import { useState, useEffect, useRef, type InputHTMLAttributes } from 'react'
import { formatPriceInput, parsePriceToKobo, koboToInputValue } from '../utils/priceUtils'

interface PriceInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  label?: string
  value: string | number // Value in kobo
  onChange: (kobo: number) => void
}

export const PriceInput = ({ label, className = '', value, onChange, ...props }: PriceInputProps) => {
  const [displayValue, setDisplayValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Initialize display value from kobo prop
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setDisplayValue(koboToInputValue(value))
    }
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value

    // Allow empty input
    if (inputValue === '') {
      setDisplayValue('')
      onChange(0)
      return
    }

    // Get digits only for formatting
    const digitsOnly = inputValue.replace(/\D/g, '')

    if (!digitsOnly) {
      return
    }

    // Format for display
    const formatted = formatPriceInput(digitsOnly)
    setDisplayValue(formatted)

    // Calculate cursor position to maintain it during formatting
    const cursorPosition = e.target.selectionStart || 0
    const oldLength = displayValue.length
    const newLength = formatted.length

    // Convert to kobo and send to parent
    const kobo = parsePriceToKobo(formatted)
    onChange(kobo)

    // Restore cursor position after state update
    setTimeout(() => {
      if (inputRef.current) {
        const lengthDiff = newLength - oldLength
        const newCursorPosition = Math.max(0, cursorPosition + lengthDiff)
        inputRef.current.setSelectionRange(newCursorPosition, newCursorPosition)
      }
    }, 0)
  }

  return (
    <div>
      {label && (
        <label className="block text-[14px] font-[600] text-foreground mb-1">{label}</label>
      )}
      <input
        ref={inputRef}
        type="text"
        {...props}
        value={displayValue}
        onChange={handleChange}
        placeholder="0.00"
        className={`w-full px-3 border border-[#D9D9D9] bg-muted text-foreground rounded-[10px] h-[30px] text-[14px] focus:outline-none focus:ring-blue-500 ${className}`}
      />
    </div>
  )
}
